import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef, type ComponentType, type ReactNode } from 'react'
import { cn, Tooltip } from '@airclou/ui'
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Menu,
  X
} from 'lucide-react'
import { createPortal } from 'react-dom'
import { getCurrentApp } from './utils/appDetection'

// For root path '/', use exact match; for other paths, also match child routes
function isNavItemActive(currentPath: string, itemHref: string): boolean {
  if (itemHref === '/' || itemHref === '/dashboard') {
    return currentPath === itemHref
  }
  return currentPath === itemHref || currentPath.startsWith(itemHref + '/')
}

// Check if a child route is active, accounting for sibling routes with shared prefixes
function isChildRouteActive(
  currentPath: string,
  childHref: string,
  siblings: NavigationItem[]
): boolean {
  if (currentPath === childHref) return true
  if (childHref !== '/dashboard' && currentPath.startsWith(childHref + '/')) {
    const hasSiblingMatch = siblings.some(sibling =>
      sibling.href !== childHref &&
      sibling.href.startsWith(childHref) &&
      currentPath.startsWith(sibling.href)
    )
    return !hasSiblingMatch
  }
  return false
}

export interface NavigationItem {
  name: string
  href: string
  icon: ComponentType<{ className?: string }>
  /**
   * A count of things waiting behind the item (a setup checklist's open
   * items). Drawn on the icon's corner when the rail is collapsed, as a pill
   * after the label otherwise; nothing is drawn for 0 or undefined.
   */
  badge?: number
}

/** The count on a nav item; see NavigationItem.badge. */
function NavBadge({ count, onIcon }: { count: number; onIcon: boolean }) {
  return (
    <span
      data-testid="nav-badge"
      className={cn(
        'inline-flex items-center justify-center rounded-full bg-primary text-[10px] font-semibold leading-none text-primary-foreground',
        onIcon ? 'absolute -right-1.5 -top-1.5 h-4 min-w-[1rem] px-1' : 'ml-auto h-5 min-w-[1.25rem] px-1.5',
      )}
    >
      {count}
    </span>
  )
}

export interface ExpandableNavigationItem {
  name: string
  icon: ComponentType<{ className?: string }>
  children: NavigationItem[]
}

export interface NavigationSection {
  title?: string
  items: NavigationItem[]
}

export interface BrandingConfig {
  name: string
  logoText: string
  logoClassName?: string
}

interface SidebarProps {
  isMobileMenuOpen?: boolean
  setIsMobileMenuOpen?: (open: boolean) => void
  branding: BrandingConfig
  navigation: NavigationItem[]
  documentSections?: NavigationSection[]
  bottomNavigation?: NavigationItem[]
  expandableItems?: ExpandableNavigationItem[]
  hideBottomNavigation?: boolean
  /** Arbitrary content rendered at the very bottom of the sidebar (below
   *  bottomNavigation). Use for app-specific pieces like a user/presence chip. */
  footerSlot?: ReactNode
  /** If true, sidebar is always collapsed and toggle button is hidden */
  alwaysCollapsed?: boolean
  /** If true, sidebar starts collapsed (user can still toggle). Overrides localStorage. */
  defaultCollapsed?: boolean
  /** Draw a rule above the expandable groups, separating "places you work"
   *  (the flat items) from "places you configure". Opt-in so apps that treat
   *  their groups as part of one continuous list are unaffected. */
  dividerBeforeExpandable?: boolean
}

export function Sidebar({
  isMobileMenuOpen = false,
  setIsMobileMenuOpen,
  branding,
  navigation,
  documentSections = [],
  bottomNavigation = [],
  expandableItems = [],
  hideBottomNavigation = false,
  footerSlot,
  alwaysCollapsed = false,
  defaultCollapsed,
  dividerBeforeExpandable = false
}: SidebarProps) {
  const location = useLocation()

  // Load initial collapse state from localStorage, unless defaultCollapsed or alwaysCollapsed is set
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (alwaysCollapsed) return true
    if (defaultCollapsed !== undefined) return defaultCollapsed
    try {
      const app = getCurrentApp()
      const key = app ? `${app}:sidebarCollapsed` : 'sidebarCollapsed'
      return localStorage.getItem(key) === 'true'
    } catch {
      return false
    }
  })
  
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const [hoveredExpandableItem, setHoveredExpandableItem] = useState<string | null>(null)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [manuallyToggled, setManuallyToggled] = useState<Set<string>>(new Set())
  const [popoverPosition, setPopoverPosition] = useState<{x: number, y: number} | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Helper functions for hover management
  const showPopover = (itemName: string, buttonElement: HTMLButtonElement) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    
    setHoveredExpandableItem(itemName)
    const rect = buttonElement.getBoundingClientRect()
    setPopoverPosition({ 
      x: rect.right + 8, 
      y: rect.top 
    })
  }

  const hidePopover = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredExpandableItem(null)
      setPopoverPosition(null)
    }, 150) // Slightly longer delay to allow smooth movement
  }

  const cancelHidePopover = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }

  // Auto-expand sections based on current route, but preserve manually toggled items
  useEffect(() => {
    const currentPath = location.pathname

    setExpandedItems(prevExpanded => {
      const newExpandedItems = new Set(prevExpanded)

      expandableItems.forEach(item => {
        // Skip if this item was manually toggled by the user
        if (manuallyToggled.has(item.name)) {
          return
        }

        // Check if any child is active
        const hasActiveChild = item.children.some(child => 
          currentPath === child.href || 
          (child.href !== '/dashboard' && currentPath.startsWith(child.href + '/'))
        )
        
        if (hasActiveChild) {
          newExpandedItems.add(item.name)
        } else {
          // Only auto-collapse if it wasn't manually expanded
          newExpandedItems.delete(item.name)
        }
      })

      return newExpandedItems
    })
  }, [location.pathname, expandableItems, manuallyToggled])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  const toggleSidebar = () => {
    // Don't allow toggling if alwaysCollapsed is true
    if (alwaysCollapsed) return
    
    const newCollapsedState = !isCollapsed
    setIsCollapsed(newCollapsedState)
    
    // Save to localStorage
    try {
      const app = getCurrentApp()
      const key = app ? `${app}:sidebarCollapsed` : 'sidebarCollapsed'
      localStorage.setItem(key, String(newCollapsedState))
    } catch {
      // Ignore localStorage errors (e.g., in private browsing mode)
    }
  }

  const closeMobileMenu = () => {
    if (setIsMobileMenuOpen) {
      setIsMobileMenuOpen(false)
    }
  }

  const toggleExpandedItem = (itemName: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemName)) {
        newSet.delete(itemName)
      } else {
        newSet.add(itemName)
      }
      return newSet
    })
    
    // Mark this item as manually toggled
    setManuallyToggled(prev => new Set(prev).add(itemName))
  }

  return (
    <>
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Sidebar - slide in from left */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-gray-50 dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800 transform transition-transform duration-300 ease-in-out lg:hidden",
        isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Fixed Header - Mobile Logo */}
          <div className="flex items-center justify-between h-[calc(4rem-1px)] border-b border-gray-200 dark:border-gray-800 px-6 flex-shrink-0">
            <div className="flex items-center">
              <div className={`w-8 h-8 bg-primary rounded-lg flex items-center justify-center mr-3 ${branding.logoClassName || ''}`}>
                <span className="text-primary-foreground font-bold text-sm">{branding.logoText}</span>
              </div>
              <p className="text-base font-semibold text-foreground">{branding.name}</p>
            </div>
            <button
              onClick={closeMobileMenu}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto">
            {/* Mobile Navigation */}
            <nav className="py-4 px-4 space-y-1">
            {navigation.map((item) => {
              const isActive = isNavItemActive(location.pathname, item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={closeMobileMenu}
                  className={cn(
                    'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white'
                  )}
                >
                  <item.icon
                    className={cn(
                      'mr-3 h-5 w-5 flex-shrink-0',
                      isActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200'
                    )}
                  />
                  {item.name}
                  {item.badge ? <NavBadge count={item.badge} onIcon={false} /> : null}
                </Link>
              )
            })}

            {/* Mobile Document Sections */}
            {documentSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="pt-6">
                {section.title && (
                  <div className="px-2 pb-2">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {section.title}
                    </h3>
                  </div>
                )}
                {section.items.map((item) => {
                  const isActive = isNavItemActive(location.pathname, item.href)
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={closeMobileMenu}
                      className={cn(
                        'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                        isActive
                          ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white'
                      )}
                    >
                      <item.icon
                        className={cn(
                          'mr-3 h-5 w-5 flex-shrink-0',
                          isActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200'
                        )}
                      />
                      <span className="flex-1">{item.name}</span>
                    </Link>
                  )
                })}
              </div>
            ))}

            {/* Mobile Expandable Items */}
            {/* Padding, not margin: the parent <nav> is `space-y-1`, whose
                `> :not([hidden]) ~ :not([hidden])` selector outranks a plain
                `.my-*` utility and would silently flatten the gap to 4px. */}
            {dividerBeforeExpandable && expandableItems.length > 0 && (
              <div className="py-2">
                <div role="separator" className="border-t border-gray-200 dark:border-gray-800" />
              </div>
            )}
            {expandableItems.map((expandableItem, expandableIndex) => {
              const isExpanded = expandedItems.has(expandableItem.name)
              return (
                <div key={expandableIndex}>
                  <button
                    onClick={() => toggleExpandedItem(expandableItem.name)}
                    className="w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md transition-colors text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white"
                  >
                    <div className="flex items-center">
                      <expandableItem.icon
                        className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200"
                      />
                      {expandableItem.name}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="ml-8 mt-1 space-y-1">
                      {expandableItem.children.map((child) => {
                        const isActive = isChildRouteActive(location.pathname, child.href, expandableItem.children)
                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            onClick={closeMobileMenu}
                            className={cn(
                              'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                              isActive
                                ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white'
                            )}
                          >
                            {child.name}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
            </nav>
          </div>

          {/* Fixed Footer */}
          <div className="flex-shrink-0">
            {/* Mobile Bottom Navigation */}
            {!hideBottomNavigation && bottomNavigation.length > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-800 py-4 px-4 space-y-1">
                {bottomNavigation.map((item) => {
                  const isActive = isNavItemActive(location.pathname, item.href)
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={closeMobileMenu}
                      className={cn(
                        'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                        isActive
                          ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white'
                      )}
                    >
                      <item.icon
                        className={cn(
                          'mr-3 h-5 w-5 flex-shrink-0',
                          isActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200'
                        )}
                      />
                      {item.name}
                    </Link>
                  )
                })}
              </div>
            )}

            {footerSlot && (
              <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-3">
                {footerSlot}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Sidebar - visible on desktop, hidden on mobile */}
      <div className={cn(
        // h-full, not h-screen: the layout's root is a full-height box that
        // shrinks under the tenant status banner (TenantStatusGate), and a
        // viewport-tall sidebar inside it put its footer below the fold.
        "flex flex-col h-full bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800 relative overflow-visible",
        "hidden lg:flex", // Hide on mobile, show on desktop
        isCollapsed ? "w-16 transition-[width] duration-300" : "w-64 transition-[width] duration-300"
      )}>
        {/* Desktop Toggle Button - hidden when alwaysCollapsed */}
        {!alwaysCollapsed && (
          <button
            onClick={toggleSidebar}
            className="absolute z-10 bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800 rounded-r-full p-1.5 transition-shadow"
            style={{ 
              top: 'calc(2rem - 14px)',
              right: '-14px'
            }}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronLeft className="h-4 w-4 text-gray-600" />
            )}
          </button>
        )}

        <div className="flex flex-col h-full overflow-visible">
          {/* Fixed Header - Logo */}
          <div className="flex items-center h-[calc(4rem-1px)] border-gray-200 dark:border-gray-800 flex-shrink-0">
            <div className={cn(
              "flex items-center transition-all duration-300",
              isCollapsed ? "ml-4" : "ml-6"
            )}>
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 bg-primary rounded-lg flex items-center justify-center ${branding.logoClassName || ''}`}>
                  <span className="text-primary-foreground font-bold text-sm">{branding.logoText}</span>
                </div>
              </div>
              <div className={cn(
                "ml-3 transition-all duration-300 overflow-hidden",
                isCollapsed ? "opacity-0 w-0" : "opacity-100 w-auto"
              )}>
                <p className="text-base font-semibold text-foreground whitespace-nowrap">{branding.name}</p>
              </div>
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto overflow-x-visible">
            {/* Main Navigation */}
            <nav className={cn(
              "py-4 space-y-1",
              isCollapsed ? "px-2 transition-[padding] duration-300" : "px-4 transition-[padding] duration-300"
            )}>
            {navigation.map((item) => {
              const isActive = isNavItemActive(location.pathname, item.href)
              
              if (isCollapsed) {
                return (
                  <Tooltip key={item.name} content={item.name} show={hoveredItem === item.name}>
                    <Link
                      to={item.href}
                      onMouseEnter={() => setHoveredItem(item.name)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={cn(
                        'flex items-center justify-center w-12 h-12 rounded-md transition-colors',
                        isActive
                          ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white'
                      )}
                    >
                      <span className="relative">
                        <item.icon
                          className={cn(
                            'h-5 w-5',
                            isActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200'
                          )}
                        />
                        {item.badge ? <NavBadge count={item.badge} onIcon /> : null}
                      </span>
                    </Link>
                  </Tooltip>
                )
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white'
                  )}
                >
                  <item.icon
                    className={cn(
                      'mr-3 h-5 w-5 flex-shrink-0',
                      isActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200'
                    )}
                  />
                  {item.name}
                  {item.badge ? <NavBadge count={item.badge} onIcon={false} /> : null}
                </Link>
              )
            })}

            {/* Desktop Document Sections */}
            {!isCollapsed && documentSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="pt-6">
                {section.title && (
                  <div className="px-2 pb-2">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {section.title}
                    </h3>
                  </div>
                )}
                {section.items.map((item) => {
                  const isActive = isNavItemActive(location.pathname, item.href)
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                        isActive
                          ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white'
                      )}
                    >
                      <item.icon
                        className={cn(
                          'mr-3 h-5 w-5 flex-shrink-0',
                          isActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200'
                        )}
                      />
                      <span className="flex-1">{item.name}</span>
                    </Link>
                  )
                })}
              </div>
            ))}

            {/* Desktop Expandable Items */}
            {/* Padding, not margin: the parent <nav> is `space-y-1`, whose
                `> :not([hidden]) ~ :not([hidden])` selector outranks a plain
                `.my-*` utility and would silently flatten the gap to 4px. */}
            {dividerBeforeExpandable && expandableItems.length > 0 && (
              <div className="py-2">
                <div role="separator" className="border-t border-gray-200 dark:border-gray-800" />
              </div>
            )}
            {expandableItems.map((expandableItem, expandableIndex) => {
              const isExpanded = expandedItems.has(expandableItem.name)
              const hasActiveChild = expandableItem.children.some(child =>
                isChildRouteActive(location.pathname, child.href, expandableItem.children)
              )

              if (isCollapsed) {
                return (
                  <div key={expandableIndex} className="relative mt-2">
                    <button
                      ref={buttonRef}
                      onMouseEnter={(e) => {
                        showPopover(expandableItem.name, e.currentTarget)
                      }}
                      onMouseLeave={hidePopover}
                      onClick={() => toggleExpandedItem(expandableItem.name)}
                      className={cn(
                        "flex items-center justify-center w-12 h-12 rounded-md transition-colors",
                        hasActiveChild
                          ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white'
                      )}
                    >
                      <expandableItem.icon className={cn(
                        "h-5 w-5",
                        hasActiveChild 
                          ? 'text-primary' 
                          : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200'
                      )} />
                    </button>
                  </div>
                )
              }

              return (
                <div key={expandableIndex}>
                  <button
                    onClick={() => toggleExpandedItem(expandableItem.name)}
                    className="w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md transition-colors text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white"
                  >
                    <div className="flex items-center">
                      <expandableItem.icon
                        className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200"
                      />
                      {expandableItem.name}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="ml-8 mt-1 space-y-1">
                      {expandableItem.children.map((child) => {
                        const isActive = isChildRouteActive(location.pathname, child.href, expandableItem.children)
                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            className={cn(
                              'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                              isActive
                                ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white'
                            )}
                          >
                            {child.name}
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
            </nav>
          </div>

          {/* Fixed Footer */}
          <div className="flex-shrink-0">
            {/* Desktop Bottom Navigation */}
            {!hideBottomNavigation && bottomNavigation.length > 0 && (
              <div className={cn(
                "border-t border-gray-200 dark:border-gray-800 py-4 space-y-1",
                isCollapsed ? "px-2 transition-[padding] duration-300" : "px-4 transition-[padding] duration-300"
              )}>
                {bottomNavigation.map((item) => {
                  const isActive = isNavItemActive(location.pathname, item.href)

                  if (isCollapsed) {
                    return (
                      <div key={item.name} className="group relative">
                        <Link
                          to={item.href}
                          className={cn(
                            'flex items-center justify-center w-12 h-12 rounded-md transition-colors',
                            isActive
                              ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white'
                          )}
                        >
                          <item.icon
                            className={cn(
                              'h-5 w-5',
                              isActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200'
                            )}
                          />
                        </Link>
                        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
                          {item.name}
                        </div>
                      </div>
                    )
                  }

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                        isActive
                          ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))]'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white'
                      )}
                    >
                      <item.icon
                        className={cn(
                          'mr-3 h-5 w-5 flex-shrink-0',
                          isActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-200'
                        )}
                      />
                      {item.name}
                    </Link>
                  )
                })}
              </div>
            )}

            {footerSlot && (
              <div className={cn(
                'border-t border-gray-200 dark:border-gray-800',
                isCollapsed ? 'px-2 py-3' : 'px-3 py-3'
              )}>
                {footerSlot}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Portal-rendered popover */}
      {hoveredExpandableItem && popoverPosition && isCollapsed && 
        expandableItems.find(item => item.name === hoveredExpandableItem) &&
        createPortal(
          <div 
            className="fixed bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-2 min-w-48"
            style={{
              left: popoverPosition.x,
              top: popoverPosition.y,
              zIndex: 99999,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onMouseEnter={cancelHidePopover}
            onMouseLeave={hidePopover}
          >
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
              {hoveredExpandableItem}
            </div>
            {expandableItems.find(item => item.name === hoveredExpandableItem)!.children.map((child) => {
              const hoveredItem = expandableItems.find(item => item.name === hoveredExpandableItem)!
              const isActive = isChildRouteActive(location.pathname, child.href, hoveredItem.children)
              return (
                <Link
                  key={child.name}
                  to={child.href}
                  className={cn(
                    'block px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-100 dark:hover:bg-gray-800',
                    isActive
                      ? 'text-primary bg-primary/10 dark:bg-primary/20'
                      : 'text-gray-700 dark:text-gray-300'
                  )}
                  onClick={() => {
                    setHoveredExpandableItem(null)
                    setPopoverPosition(null)
                    if (hoverTimeoutRef.current) {
                      clearTimeout(hoverTimeoutRef.current)
                      hoverTimeoutRef.current = null
                    }
                  }}
                >
                  {child.name}
                </Link>
              )
            })}
          </div>,
          document.body
        )
      }
    </>
  )
}

export function MobileMenuButton({ isOpen, onClick }: { isOpen: boolean; onClick: () => void }) {
  const { t } = useTranslation()
  
  return (
    <button
      type="button"
      className="p-2 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
      onClick={onClick}
    >
      <span className="sr-only">{t('sidebar.openMenu')}</span>
      {isOpen ? (
        <X className="block h-6 w-6" aria-hidden="true" />
      ) : (
        <Menu className="block h-6 w-6" aria-hidden="true" />
      )}
    </button>
  )
}

export default Sidebar